import { useContext, useImperativeHandle, useRef, RefObject } from 'react';

import { useTriggerRepaint } from '@cast/utils';

import { useExpandableRows } from './_hooks/useExpandableRows';
import { useFlexibleColumns } from './_hooks/useFlexibleColumns';
import { useReorderableRows } from './_hooks/useReorderableRows';
import { useSorting } from './_hooks/useSorting';
import { useTreeRows } from './_hooks/useTreeRows';
import { parseColumnProps } from './_utils';
import { initializeColumns } from './initializeColumns';
import { TablePersistence } from '../../persistence/context';
import {
  NormalizedTableProps,
  TableApi,
  TableState,
  RowKey,
} from '../../types';

export const useTableComponent = <T, K extends RowKey = RowKey>(
  normalizedProps: NormalizedTableProps<any, any>,
  rootRect: Pick<DOMRect, 'width' | 'height'> | null
): RefObject<TableApi<T, K>> => {
  const { children, apiRef } = normalizedProps;
  const forceRepaint = useTriggerRepaint();
  const persistenceCtx = useContext(TablePersistence);

  const tableApiRef = useRef<TableApi>({
    state: {} as never,
    setState: (
      newState: TableState | ((newState: TableState) => TableState),
      repaint = true
    ) => {
      const tableApi = tableApiRef.current;
      const oldState = tableApi?.state;
      if (typeof newState === 'function') {
        tableApiRef.current.state = newState(oldState);
      } else {
        tableApiRef.current.state = newState;
      }
      persistenceCtx.accept(tableApiRef.current);
      if (repaint) {
        forceRepaint();
      }
    },
    // TODO (Justas): Remove this, to keep the API simple. Used in a few places and achievable by simply using `useElementHeight`
    tableInfo: {
      bodyHeight: 0,
      setBodyHeight: (
        height: number | ((height: number) => number),
        repaint?: boolean
      ) => {
        const tableApi = tableApiRef.current;
        const oldHeight = tableApi.tableInfo?.bodyHeight ?? 0;
        if (typeof height === 'function') {
          tableApi.tableInfo.bodyHeight = height(oldHeight);
        } else {
          tableApi.tableInfo.bodyHeight = height;
        }
        if (repaint) {
          forceRepaint();
        }
      },
    },
    log: normalizedProps.log,
    // TableApi is fully initialized in the same render. API object is decorated by plugin hooks to keep the separation of concerns
  } as unknown as TableApi);
  const isStateRestored = useRef(false);

  useImperativeHandle(apiRef, () => tableApiRef.current, [tableApiRef]);

  const columnsProps = parseColumnProps(children);
  useFlexibleColumns(tableApiRef, normalizedProps);
  initializeColumns({
    tableApiRef,
    groupedColumnsProps: columnsProps,
    containerWidth: rootRect?.width,
    tableProps: normalizedProps,
  });

  useExpandableRows(tableApiRef, normalizedProps);
  useTreeRows(tableApiRef, normalizedProps);
  useSorting(tableApiRef, normalizedProps);
  useReorderableRows(tableApiRef, normalizedProps);

  if (!isStateRestored.current) {
    tableApiRef.current.state = persistenceCtx.retrieve(tableApiRef.current);
    tableApiRef.current.restoreDefaults = () => {
      tableApiRef.current.setColumnSettings(() => {
        return {
          resizedColumns: {},
          columnsOrder: [],
          hiddenColumns: {},
          lockedColumns: {},
        };
      });
    };
    isStateRestored.current = true;
  }

  return tableApiRef as unknown as RefObject<TableApi<T, K>>;
};
